import React from 'react'
import { addMonths } from 'date-fns/addMonths'
import { addDays } from 'date-fns/addDays'
import { addYears } from 'date-fns/addYears'
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays'
import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths'
import { endOfDay } from 'date-fns/endOfDay'
import { endOfMonth } from 'date-fns/endOfMonth'
import { endOfToday } from 'date-fns/endOfToday'
import { endOfYear } from 'date-fns/endOfYear'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { startOfYear } from 'date-fns/startOfYear'
import { subMonths } from 'date-fns/subMonths'
import { subDays } from 'date-fns/subDays'
import { subYears } from 'date-fns/subYears'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { useTheme } from '@mui/material/styles'

import { DateRange } from '@mui/x-date-pickers-pro'

import { DateRangePicker, DateRangePickerCopy, Icon, Text } from '@mxenabled/mxui'

import { DATE_FORMATS_INTL, formatDate } from '../../../core/constants'
import { useScreenSize } from '../../hooks'
import { useAriaLive } from '../../hooks/useAriaLive'
import Dialog from '../dialog/Dialog'
import Drawer from '../drawer/Drawer'

export type DateRangeSelectorCopy = {
  dateRangePickerCopy: DateRangePickerCopy
  dateRangeEditApply: string
  dateRangeEditTitle: string
  dateRangeNextAria: string
  dateRangePreviousAria: string
  dateRangeCloseAria: string
}

export type CalendarActions = {
  shouldDisableNext?: boolean
  shouldDisablePrevious?: boolean
  shouldDisablePicker?: boolean
  onRangeChanged: (dateRange: DateRange<Date>) => void
  /**
   * Fires only when the user actively selects a timeframe button/tab (3M/6M/9M/1Y).
   * Unlike onRangeChanged, this does NOT fire on mount, so it's safe for click analytics.
   */
  onTimeframeClick?: (timeframe: number) => void
}

export interface DateRangeSelectorProps {
  calendarActions: CalendarActions
  color?: 'primary' | 'secondary'
  copy: DateRangeSelectorCopy
  range?: DateRange<Date>
  shouldShrinkToIcon?: boolean
  variant?: 'outlined' | 'text' | 'readonly' | 'timeframebuttons' | 'timeframetabs'
}

export type Timeframe = {
  ariaLabel?: string
  label: string
  value: number
}

export const DEFAULT_TIMEFRAME = 6
const SUPPORTED_TIMEFRAMES = [3, 6, 9, 12]

/**
 * Derive the timeframe button/tab that matches a caller-supplied range.
 *
 * The timeframe variants own a `timeframe` state that drives the mount effect.
 * Seeding it from the incoming range keeps the control in sync with the
 * caller's stored selection, so remounting doesn't reset it to the default
 * and clobber that selection via onRangeChanged. (MEW-3911)
 *
 * Returns DEFAULT_TIMEFRAME when the range doesn't correspond to a button
 * (e.g. NetWorth, which renders the timeframe variants without passing a
 * range and relies on the mount emit to seed its own 6M default).
 */
export const getTimeframeFromRange = (range: DateRange<Date>): number => {
  const [start, end] = range
  if (!start || !end) return DEFAULT_TIMEFRAME

  const months = differenceInCalendarMonths(end, start)
  // onRangeChanged consumers add a second to the start date, which drops the
  // calendar-month difference by one, so accept both N and N-1.
  return SUPPORTED_TIMEFRAMES.find((tf) => months === tf || months === tf - 1) ?? DEFAULT_TIMEFRAME
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  calendarActions,
  color = 'primary',
  copy,
  range = [startOfMonth(startOfToday()), endOfMonth(endOfToday())],
  shouldShrinkToIcon = true,
  variant = 'outlined',
}) => {
  const theme = useTheme()
  const { isMobile } = useScreenSize()
  const { announce, ariaLive } = useAriaLive()

  const [dateRange, setDateRange] = React.useState<DateRange<Date>>(range)
  const [shortcut, setShortcut] = React.useState(copy.dateRangePickerCopy.dateRangeThisMonth)
  const [pickerDateRange, setPickerDateRange] = React.useState(dateRange)
  const [isPickerOpen, setIsPickerOpen] = React.useState(false)
  const [isMonthFormat, setIsMonthFormat] = React.useState(true)
  const [timeframe, setTimeframe] = React.useState(() => getTimeframeFromRange(range))

  // TODO: put labels in mrs content and use as copy
  const timeframes = {
    buttons: [
      { ariaLabel: '3 Months', label: '3M', value: 3 },
      { ariaLabel: '6 Months', label: '6M', value: 6 },
      { ariaLabel: '9 Months', label: '9M', value: 9 },
      { ariaLabel: '1 Year', label: '1Y', value: 12 },
    ],
    tabs: [
      { label: '3 Months', value: 3 },
      { label: '6 Months', value: 6 },
      { label: '9 Months', value: 9 },
      { label: '1 Year', value: 12 },
    ],
  }

  // Depends on the range timestamps rather than the array identity: callers such
  // as WidgetDateRangeSelector pass an inline `[start, end]` literal, so keying
  // on identity re-runs this on every parent render. (MEW-3910, MEW-3911)
  React.useEffect(() => {
    setDateRange(range)
    setPickerDateRange(range)
    // Keep the highlighted timeframe in sync when the range changes without a
    // remount, otherwise the control keeps highlighting the previous timeframe
    // and clicking it is a no-op (React bails out of identical state). (MEW-3911)
    setTimeframe(getTimeframeFromRange(range))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range[0]?.getTime(), range[1]?.getTime()])

  // dates for timeframe buttons
  // using today's date as the end date, looks back 3, 6, 9, or 12 months
  React.useEffect(() => {
    if (variant === 'timeframebuttons' || variant === 'timeframetabs') {
      calendarActions.onRangeChanged(getTimeFrameDates())
    }
  }, [timeframe])

  const getTimeFrameDates = () => {
    const timeframeEndDate = endOfMonth(endOfToday()) as Date
    const timeframeBeginDate = endOfMonth(subMonths(timeframeEndDate, timeframe))
    return [timeframeBeginDate, timeframeEndDate] as DateRange<Date>
  }

  const handlePrevNextClick = (direction: 'previous' | 'next') => {
    const [beginDate, endDate] = dateRange as [Date, Date]
    const isPrev = direction === 'previous'

    let newRange: DateRange<Date>

    // Month-based shortcuts
    if (
      shortcut === copy.dateRangePickerCopy.dateRangeThisMonth ||
      shortcut === copy.dateRangePickerCopy.dateRangeLastMonth
    ) {
      newRange = [
        isPrev ? subMonths(beginDate, 1) : addMonths(beginDate, 1),
        endOfMonth(isPrev ? subMonths(endDate, 1) : addMonths(endDate, 1)),
      ]
    }
    // Year-based shortcuts
    else if (
      shortcut === copy.dateRangePickerCopy.dateRangeYTD ||
      shortcut === copy.dateRangePickerCopy.dateRangeLastYear
    ) {
      const startYear = startOfYear(beginDate)
      const endYear = endOfYear(beginDate)
      newRange = [
        isPrev ? subYears(startYear, 1) : addYears(startYear, 1),
        isPrev ? subYears(endYear, 1) : addYears(endYear, 1),
      ]
    }
    // Custom date range - shift by the current range duration
    else {
      const dayOffset = differenceInCalendarDays(endDate, beginDate)
      const dateModifier = isPrev ? subDays : addDays
      newRange = [dateModifier(beginDate, dayOffset), dateModifier(endDate, dayOffset)]
    }

    setDateRange(newRange)
    calendarActions.onRangeChanged(newRange)
  }

  const handleRangeChanged = () => {
    setDateRange(pickerDateRange)
    setIsMonthFormat(
      shortcut === copy.dateRangePickerCopy.dateRangeThisMonth ||
        shortcut === copy.dateRangePickerCopy.dateRangeLastMonth,
    )
    calendarActions.onRangeChanged(pickerDateRange)
  }

  const handleDataRangePickerChange = (range: DateRange<Date>, shortcut: string) => {
    range[1] = range[1] ?? range[0]

    const adjustedRange: DateRange<Date> = [range[0], range[1] ? endOfDay(range[1]) : range[1]]
    const startFormatted = formatDate(adjustedRange[0] as Date, DATE_FORMATS_INTL.MONTH_DAY_YEAR)
    const endFormatted = formatDate(adjustedRange[1] as Date, DATE_FORMATS_INTL.MONTH_DAY_YEAR)

    setPickerDateRange(adjustedRange)
    setShortcut(shortcut)
    announce(`${copy.dateRangeEditTitle}: ${startFormatted} - ${endFormatted}`)
  }

  const handleTimeframeChanged = (timeframe: number) => {
    setTimeframe(timeframe)
    calendarActions.onTimeframeClick?.(timeframe)
  }

  return (
    <Box className="mx-cmn-date-range-selector" m={4}>
      <Box
        sx={{
          [theme.breakpoints.down('sm')]: {
            display: shouldShrinkToIcon ? 'none' : 'flex',
            justifyContent: 'center',
          },
          [theme.breakpoints.up('sm')]: { display: 'inline-flex' },
        }}
      >
        {variant === 'readonly' && (
          <Stack alignItems="center" direction="row" gap={8}>
            <Icon name="calendar_month" size={24} />
            <Text bold={true}>
              {formatDate(pickerDateRange[0] as Date, DATE_FORMATS_INTL.MONTH_DAY)}
            </Text>
            <Icon name="remove" />
            <Text bold={true}>
              {formatDate(pickerDateRange[1] as Date, DATE_FORMATS_INTL.MONTH_DAY_YEAR)}
            </Text>
          </Stack>
        )}

        {variant !== 'readonly' &&
          variant !== 'timeframebuttons' &&
          variant !== 'timeframetabs' && (
            <ButtonGroup
              color={color}
              sx={{
                '& .MuiButtonGroup-grouped': {
                  padding: isMobile ? 0 : 12,
                },
                '& .MuiButtonGroup-middleButton': {
                  px: 24,
                },
                height: 44,
              }}
              variant={variant}
            >
              <Button
                aria-disabled={calendarActions.shouldDisablePrevious}
                aria-label={copy.dateRangePreviousAria}
                color={calendarActions.shouldDisablePrevious ? 'secondary' : 'primary'}
                onClick={() =>
                  calendarActions.shouldDisablePrevious ? null : handlePrevNextClick('previous')
                }
              >
                <Icon name="chevron_left" size={20} />
              </Button>
              <Button
                aria-disabled={calendarActions.shouldDisablePicker}
                color={color}
                onClick={() => (calendarActions.shouldDisablePicker ? null : setIsPickerOpen(true))}
                startIcon={<Icon name="calendar_month" size={20} />}
                sx={{
                  p: isMobile ? 0 : 8,
                }}
              >
                {isMonthFormat ? (
                  formatDate(dateRange[0] as Date, DATE_FORMATS_INTL.MONTH_YEAR)
                ) : (
                  <span>
                    {formatDate(dateRange[0] as Date, DATE_FORMATS_INTL.MONTH_DAY_YEAR)} -{' '}
                    {formatDate(dateRange[1] as Date, DATE_FORMATS_INTL.MONTH_DAY_YEAR)}
                  </span>
                )}
              </Button>
              <Button
                aria-disabled={calendarActions.shouldDisableNext}
                aria-label={copy.dateRangeNextAria}
                color={calendarActions.shouldDisableNext ? 'secondary' : 'primary'}
                onClick={() =>
                  calendarActions.shouldDisableNext ? null : handlePrevNextClick('next')
                }
              >
                <Icon name="chevron_right" size={20} />
              </Button>
            </ButtonGroup>
          )}

        {variant === 'timeframebuttons' && (
          <ToggleButtonGroup
            aria-label={copy.dateRangeEditTitle}
            color={color}
            exclusive={true}
            orientation="horizontal"
            value={timeframe}
          >
            {timeframes.buttons.map((timeframe: Timeframe) => (
              <ToggleButton
                aria-label={timeframe.ariaLabel}
                key={timeframe.label}
                onClick={() => handleTimeframeChanged(timeframe.value)}
                sx={{ width: 56 }}
                value={timeframe.value}
              >
                {timeframe.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        {variant === 'timeframetabs' && (
          <Tabs
            indicatorColor="primary"
            onChange={(_, newValue) => handleTimeframeChanged(newValue)}
            textColor="primary"
            value={timeframe}
            variant="fullWidth"
          >
            {timeframes.tabs.map((timeframe: Timeframe) => (
              <Tab
                key={timeframe.label}
                label={
                  <Text bold={true} variant="body1">
                    {timeframe.label}
                  </Text>
                }
                sx={{ minWidth: 80 }}
                value={timeframe.value}
              />
            ))}
          </Tabs>
        )}
      </Box>

      {isPickerOpen && isMobile && (
        <Drawer
          ariaLabelClose={copy.dateRangeCloseAria}
          isOpen={isPickerOpen}
          onClose={() => setIsPickerOpen(false)}
          onPrimaryAction={() => {
            handleRangeChanged()
            setIsPickerOpen(false)
          }}
          primaryText={copy.dateRangeEditApply}
          title={copy.dateRangeEditTitle}
        >
          {ariaLive}
          <DateRangePicker
            copy={copy.dateRangePickerCopy}
            dateRange={pickerDateRange}
            onRangeChanged={handleDataRangePickerChange}
            selectedShortcut={shortcut}
            sx={{ p: 0 }}
          />
        </Drawer>
      )}

      {isPickerOpen && !isMobile && (
        <Dialog
          copy={{ title: copy.dateRangeEditTitle }}
          isOpen={true}
          maxWidth="sm"
          onClose={() => setIsPickerOpen(false)}
          onPrimaryAction={() => handleRangeChanged()}
          primaryText={copy.dateRangeEditApply}
          sx={{
            '& .MuiDialogContent-root': {
              padding: 0,
            },
            '& .MuiDialog-paper': {
              minWidth: 'max-content',
            },
          }}
        >
          {ariaLive}
          <DateRangePicker
            copy={copy.dateRangePickerCopy}
            dateRange={pickerDateRange}
            onRangeChanged={handleDataRangePickerChange}
            selectedShortcut={shortcut}
            sx={{
              pt: 0,
              '& .MuiPickersLayout-toolbar': {
                gridColumn: '1/4',
              },
            }}
          />
        </Dialog>
      )}
    </Box>
  )
}

export default DateRangeSelector
